export const ROUTES = {
  // Top-level sections
  analytics: "/analytics",
  analyticsKpis: "/analytics/kpis",
  prospect: "/prospect",
  lifeEvents: "/life-events",
  engagements: "/engagements",
  engagementsDashboard: "/engagements/dashboard",
  conversation: "/conversation",
  inventory: "/inventory",
  personalizedAds: "/personalized-ads",
  adminDashboard: "/admin-dashboard",
  settings: "/settings",
  callDetail: "/call-detail",
  mediaGallery: "/media-gallery",
  noPermission: "/no-permission",

  reports: "/reports",
  dailyCheckoutReport: "/daily-checkout-report",
  scheduledReports: "/scheduled-reports",
  // Reports ▸ Media Report — standalone entry point for the same detailed
  // media report that Dashboard ▸ Media tracking ▸ "View detailed report"
  // opens at `mediaTrackingDetailReport` (inside the Engagements shell).
  mediaReport: "/media-report",
  emailAnalytics: "/email-analytics",
  departmentReports: "/reports/department",
  employeeReports: "/reports/employee/:employeeId",
  locationReports: "/reports/location/:locationId",
  emailLocationAnalytics: "/email-analytics/location/:locationId",
  emailTypeAnalytics: "/email-analytics/type/:typeId",
  ottoAnalytics: "/otto-analytics",
  ottoMetrics: "/otto-analytics/:metricType",
  // OttoVin vehicle/content-engagement reports (mirrors OttoVin's /analytics).
  // Lead/vehicle drilldowns are in-page modals, so no sub-routes are needed.
  ottoVinReports: "/ottovin-reports",
  abandonedCalls: "/abandoned-calls",
  abandonedCallsList: "/abandoned-calls/calls",
  abandonedCallsTasks: "/abandoned-calls/tasks",
  // New unified report tabs routes
  reportAbandonedCalls: "/reports/abandoned-calls",
  reportCallTasks: "/reports/call-tasks",
  engagementsAppointment: "/engagements/appointment",
  engagementsLeadCenter: "/engagements/lead-center",
  engagementsEmail: "/engagements/email",
  engagementsTasks: "/engagements/tasks",
  engagementsTemplates: "/engagements/templates",
  engagementsAutoresponderFeedback: "/engagements/autoresponder-feedback",
  reviews: "/reviews",
  activityCenter: "/engagements/activity-center",
  defaultRoute: "/engagements/activity-center/",
  mediaTrackingDetailReport: "/engagements/dashboard/media-tracking-details",
  messageSentReceivedDetailReport: "/engagements/dashboard/message-sent-received-details",
  reviewDetailReport: "/engagements/dashboard/review-detail-report",
  communicationDetailReport: "/engagements/dashboard/communication-detail",
};

// Tab keys for the Communication Detail screen. Origin tiles pass one of
// these as `?tab=...` so the destination lands on the matching table.
export const COMMUNICATION_DETAIL_TABS = {
  SALES_PERSON: "sales-person",
  SOURCE: "source",
};

/**
 * Get the actual path by replacing dynamic parameter in the route
 * @param {string} route - The route path with dynamic parameters (e.g. '/reports/employee/:employeeId')
 * @param {string} id - The ID to replace in the path
 * @returns {string} The actual path with parameter replaced
 *
 * Example usage:
 * getRoutePath('/reports/employee/:employeeId', '123') => '/reports/employee/123'
 */
/**
 * Build a route URL and navigate to it, appending a query string from the
 * given params object. Keys with `null`/`undefined`/`""` values are dropped.
 * Array values become repeated keys (`?k=a&k=b`).
 *
 *   navigateWithQuery(ROUTES.messageSentReceivedDetailReport, {
 *     source: "communication_by_sales_person",
 *   });
 *   // → navigates to "/engagements/dashboard/message-sent-received-details
 *   //                ?source=communication_by_sales_person"
 *
 * Imported here to keep route construction next to ROUTES — callers pass a
 * navigate function in (avoids a hard dependency on gatsby from this file).
 *
 * @param {string}   basePath  One of ROUTES.* (or any path).
 * @param {object}   params    Plain object of query params.
 * @param {Function} navigate  Navigate fn (e.g. gatsby's `navigate`).
 */
export const navigateWithQuery = (basePath, params, navigate) => {
  if (!basePath) return;
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    if (Array.isArray(value)) {
      value.forEach(v => {
        if (v !== null && v !== undefined && v !== "") usp.append(key, String(v));
      });
    } else {
      usp.append(key, String(value));
    }
  });
  const qs = usp.toString();
  const url = qs ? `${basePath}?${qs}` : basePath;
  if (typeof navigate === "function") navigate(url);
  else if (typeof window !== "undefined") window.location.assign(url);
};

export const getRoutePath = (route, id) => {
  if (!route) {
    console.error("Route path is required");
    return "/";
  }

  let path = route;

  // Simple string replacement for the ID
  if (id !== undefined) {
    path = path.replace(/:\w+/, id);
  }

  return path;
};
